import { FC, useId } from "react"
import {
  RadioGroup as ShadRadioGroup,
  RadioGroupItem as ShadRadioGroupItem,
} from "@/components/ui/radio-group"
import { Label } from "@/components/ui/label"
import { cn } from "@/lib/utils"

export type RadioGroupProps = {
  className?: string
  label?: string
  options: RadioGroupOption[]
  value?: string
  direction?: "row" | "col"
  disabled?: boolean
  truncate?: boolean
  onChange?: (value: string) => void
}

export type RadioGroupOption = {
  id?: string
  label: string
  value: string
  disabled?: boolean
}

const RadioGroup: FC<RadioGroupProps> = ({
  className,
  label,
  options,
  value,
  direction = "row",
  disabled,
  truncate,
  onChange,
}) => {
  const id = useId()

  function optionRefId(optionId: string) {
    return `${id}.${optionId}`
  }

  return (
    <div className={cn(className)}>
      {label && (
        <Label
          className="text-label-text mb-3 text-nowrap font-medium"
          htmlFor={id}
        >
          {label}
        </Label>
      )}

      <ShadRadioGroup
        id={id}
        className={cn([
          "flex flex-wrap gap-x-8 gap-y-4",
          direction === "col" && "flex-col",
        ])}
        value={value}
        aria-label={label}
        disabled={disabled}
        onValueChange={onChange}
      >
        {options.map((option) => (
          <div
            key={option.value}
            className="flex items-center gap-3 hover:cursor-pointer"
          >
            <ShadRadioGroupItem
              className="peer"
              value={option.value}
              id={optionRefId(option.id || option.value)}
              disabled={option.disabled}
            />
            <Label
              htmlFor={optionRefId(option.id || option.value)}
              className={cn(
                `
                  text-button-black block max-w-50
                  text-sm leading-6
                  peer-aria-checked:font-medium 
                `,
                truncate && "truncate"
              )}
            >
              {option.label}
            </Label>
          </div>
        ))}
      </ShadRadioGroup>
    </div>
  )
}

export default RadioGroup
